import { z } from 'zod';

export const CONTACT_TYPES = ['billing', 'alternate'] as const;
export type ContactType = (typeof CONTACT_TYPES)[number];

/**
 * Customer contact — one contact person on a customer. `primary` is the wire
 * name for the `is_primary` column. `rowStatus` is 'active' for every contact
 * the list endpoint returns (deleted contacts are 'inactive' and filtered out).
 */
export const CustomerContactSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  customerId: z.string().uuid(),
  firstName: z.string(),
  lastName: z.string(),
  preferredName: z.string().nullable(),
  title: z.string().nullable(),
  email: z.string().nullable(),
  workPhone: z.string().nullable(),
  mobile: z.string().nullable(),
  address1: z.string().nullable(),
  address2: z.string().nullable(),
  locality: z.string().nullable(),
  region: z.string().nullable(),
  country: z.string().nullable(),
  postalCode: z.string().nullable(),
  primary: z.boolean(),
  contactType: z.enum(CONTACT_TYPES).nullable(),
  /** Bare email addresses CC'd on this contact's invoice emails. */
  ccEmails: z.array(z.string()),
  rowStatus: z.enum(['active', 'inactive']),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type CustomerContact = z.infer<typeof CustomerContactSchema>;

// firstName + lastName required; everything else optional. Optional text
// fields accept null to mean "not provided".
export const CreateCustomerContactInput = z.object({
  firstName: z.string(),
  lastName: z.string(),
  preferredName: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  workPhone: z.string().nullable().optional(),
  mobile: z.string().nullable().optional(),
  address1: z.string().nullable().optional(),
  address2: z.string().nullable().optional(),
  locality: z.string().nullable().optional(),
  region: z.string().nullable().optional(),
  country: z.string().nullable().optional(),
  postalCode: z.string().nullable().optional(),
  primary: z.boolean().optional(),
  contactType: z.enum(CONTACT_TYPES).nullable().optional(),
  ccEmails: z.array(z.string()).optional(),
});
export type CreateCustomerContactInput = z.infer<typeof CreateCustomerContactInput>;

export const PatchCustomerContactInput = CreateCustomerContactInput.partial();
export type PatchCustomerContactInput = z.infer<typeof PatchCustomerContactInput>;

/** One row of the contacts import report, in upload order. */
export type ImportRowOutcome = 'created' | 'updated' | 'failed';

export interface ImportContactsRow {
  rowNumber: number;
  qbId: string | null;
  name: string | null;
  outcome: ImportRowOutcome;
  errors?: string[];
}

export interface ImportContactsResult {
  totalRows: number;
  created: number;
  updated: number;
  failed: number;
  rows: ImportContactsRow[];
}
